import { call, put, take } from 'redux-saga/effects';
import * as actions from '../actions';
import { logCustomEvent } from '../api/analytics';
import history from '../browserHistory';
import { fetchNpsPrompt } from '../domain/NpsReview';
import { apiMethodsConst } from '../services/methods';
import { getShouldUseNpsIdentity } from '../services/posthog/FeatureFlagUtils';
import { store } from '../store/configureStore';
import { getRedirectTarget } from '../utils/AdjustmentUtil';
import { callAndDispatch, callAndDispatchPayload } from './utils';

export function* watchfeedbackRequired() {
  const events = actions.createRequestTypes(actions.FEEDBACK_REQUIRED);
  while (true) {
    // const feedbackRequiredDate =  store.getState().feedback

    yield take(actions.FEEDBACK_REQUIRED);
    yield put({ type: events.REQUEST });
    try {
      yield* callAndDispatch(apiMethodsConst.FEEDBACK_REQUEST);
      yield put({ type: events.SUCCESS });

      const job = store.getState().feedback.feedbackRequired?.[0];
      const { id: jobId, adjustments } = job;

      if (!jobId || !adjustments) {
        return;
      }

      const redirectTarget = getRedirectTarget(adjustments);
      if (redirectTarget === 'feedback-rating') {
        history.push(`/dashboard/job/${jobId}/feedback-rating`);
      } else if (redirectTarget === 'feedback') {
        history.push(`/dashboard/job/${jobId}/feedback`);
      }
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error.response?.data });
    }
  }
}

/**
 * Whether to open the NPS screen after a review. Flag off: the monolith's
 * `show_nps` on the signed-in user. Flag on: auth-service decides, and a failed
 * call means "don't show", so the review flow never breaks on it (GOTU-16475).
 */
function* shouldShowNps() {
  if (!getShouldUseNpsIdentity()) {
    return Boolean(store.getState().user.user.show_nps);
  }

  try {
    const prompt = yield call(fetchNpsPrompt);
    return prompt?.show === true;
  } catch {
    return false;
  }
}

/**
 * One feedbackSave pass, deduped per job for the session: the BID EOD flow
 * (GOTU-5643) dispatches the action twice — at Send so the rating persists
 * immediately, and again from the success handler for the message and
 * navigation — and the second pass must not re-POST the rating. A job is
 * marked saved only after the POST succeeds, so a failed save is retried by
 * the next dispatch instead of being skipped.
 */
export function* handleFeedbackSave(savedJobIds, { data, jobId, showMessage }) {
  const events = actions.createRequestTypes(actions.FEEDBACK_SAVE_ACTION);
  yield put({ type: events.REQUEST });
  try {
    if (!savedJobIds.has(jobId)) {
      yield call(callAndDispatchPayload, apiMethodsConst.FEEDBACK_SAVE_REQUEST, { data, jobId });
      savedJobIds.add(jobId);
      yield call(logCustomEvent, 'send_review', {
        ...data,
        jobId,
      });
    }

    if (showMessage) {
      const isNPSNeeded = yield* shouldShowNps();
      yield put({
        type: actions.SHOW_MESSAGE,
        payload: {
          message:
            'Your feedback has been saved. Thank you for helping us improve your experience.',
          title: 'Feedback Successfully Sent!',
          route: isNPSNeeded ? '/dashboard/nps' : '/',
          isSuccess: true,
          modalType: 'feedbackSuccess', // Used for stable test selectors
        },
      });
    }

    yield put({ type: events.SUCCESS });
  } catch (error) {
    yield put({ type: events.FAILURE, payload: error });
  }
}

export function* watchFeedbackSave() {
  const savedJobIds = new Set();
  while (true) {
    const payload = yield take(actions.FEEDBACK_SAVE_ACTION);
    yield* handleFeedbackSave(savedJobIds, payload);
  }
}

export function* watchSendProcedureEndorsement() {
  const events = actions.createRequestTypes(actions.SEND_PROCEDURE_ENDORSEMENT);

  while (true) {
    const { endorsement, userId } = yield take(actions.SEND_PROCEDURE_ENDORSEMENT);

    yield put({ type: events.REQUEST });

    try {
      yield* callAndDispatchPayload(apiMethodsConst.SEND_PROCEDURE_ENDORSEMENT_REQUEST, {
        endorsement,
        userId,
      });

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error });
    }
  }
}

export function* watchFetchUserProcedureEndorsement() {
  const events = actions.createRequestTypes(actions.FETCH_USER_PROCEDURE_ENDORSEMENT);

  while (true) {
    const { candidateId, profession } = yield take(actions.FETCH_USER_PROCEDURE_ENDORSEMENT);

    yield put({ type: events.REQUEST });

    try {
      yield* callAndDispatch(apiMethodsConst.FETCH_USER_PROCEDURE_ENDORSEMENT_REQUEST, {
        candidateId,
        profession,
      });

      yield put({ type: events.SUCCESS });
    } catch (error) {
      yield put({ type: events.FAILURE, payload: error });
    }
  }
}
